import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { CircularProgress } from '@material-ui/core';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';

import { resolveDeclinationStatus } from '../DeclinationReasonsDrawer/DeclinationReasons.shared';
import { SendoutDeclinationReasonsDrawer } from '../SendoutDeclinationReasonsDrawer';

import { LoaderContainer } from './SendoutDetailsEditDrawer.styled';
import {
  buildSendoutDetailsEditDefaultValues,
  buildSendoutDetailsEditPayload,
  hasDeclinationReasons,
  isDeclinationStatus
} from './SendoutDetailsEditDrawer.utils';
import { SendoutDetailsEditForm } from './SendoutDetailsEditForm';
import { FORM_FIELDS_MAP } from './SendoutDetailsEditForm.constants';
import { useSendoutDetailsEditData } from './useSendoutDetailsEditData';

const EMPTY_DECLINATION_DRAWER_STATE = {
  isOpen: false,
  pendingStatus: null,
  initialValues: null
};

export const SendoutDetailsEditDrawer = ({
  jobOrder = null,
  isSubmitting = false,
  onClose = () => {},
  onSaved = () => {},
  sendoutId = null
}) => {
  const form = useForm();
  const { getValues, handleSubmit, reset, setValue, triggerValidation } = form;

  const [declinationDrawerState, setDeclinationDrawerState] = useState(
    EMPTY_DECLINATION_DRAWER_STATE
  );
  const [declinationDetailsStatusId, setDeclinationDetailsStatusId] = useState(null);

  const { state } = useSendoutDetailsEditData({ sendoutId });

  const sendout = state.results?.sendout || null;
  const candidate = state.results?.candidate || null;

  const isFetching = state.status === UIStatus.Loading;
  const hasErrorInInitialFetching = state.status === UIStatus.Error;
  const isReady = state.status === UIStatus.Success;

  useEffect(() => {
    if (state.status !== UIStatus.Success) return;

    reset(buildSendoutDetailsEditDefaultValues({ sendout, jobOrder, candidate }));
    setDeclinationDetailsStatusId(sendout?.status?.id ?? null);
  }, [state.status, sendout, jobOrder, candidate, reset]);

  const closeDeclinationDrawer = () => {
    setDeclinationDrawerState(EMPTY_DECLINATION_DRAWER_STATE);
  };

  const handleStatusSelected = nextStatus => {
    const currentStatus = getValues(FORM_FIELDS_MAP.Status.key);

    if (!isDeclinationStatus(nextStatus?.id)) {
      setValue(FORM_FIELDS_MAP.DeclinationDetails.key, null, true);
      setDeclinationDetailsStatusId(null);
      closeDeclinationDrawer();
      return nextStatus;
    }

    const isSameDeclinationKind =
      resolveDeclinationStatus(declinationDetailsStatusId) ===
      resolveDeclinationStatus(nextStatus?.id);

    setDeclinationDrawerState({
      isOpen: true,
      pendingStatus: nextStatus,
      initialValues: isSameDeclinationKind
        ? getValues(FORM_FIELDS_MAP.DeclinationDetails.key)
        : null
    });

    return currentStatus || null;
  };

  const handleDeclinationDrawerSaved = async ({ payload }) => {
    const savedForStatus = declinationDrawerState.pendingStatus;

    if (!savedForStatus) return;

    setValue(FORM_FIELDS_MAP.DeclinationDetails.key, payload?.reasonDetails || null, true);
    setValue(FORM_FIELDS_MAP.Status.key, savedForStatus, true);
    setDeclinationDetailsStatusId(savedForStatus?.id ?? null);
    closeDeclinationDrawer();
    await triggerValidation(FORM_FIELDS_MAP.Status.key);
  };

  const handleSave = async formData => {
    if (
      isDeclinationStatus(formData?.[FORM_FIELDS_MAP.Status.key]?.id) &&
      !hasDeclinationReasons(formData?.[FORM_FIELDS_MAP.DeclinationDetails.key])
    ) {
      setDeclinationDrawerState({
        isOpen: true,
        pendingStatus: formData?.[FORM_FIELDS_MAP.Status.key] || null,
        initialValues: null
      });
      return;
    }

    await onSaved({
      payload: buildSendoutDetailsEditPayload(formData)
    });
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: onClose,
        disabled: isSubmitting
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled: isSubmitting || !isReady,
        isSaving: isSubmitting
      },
      GridItemProps: { md: 3 },
      mode: 'save'
    }
  ];

  const handleClose = isSubmitting ? () => {} : onClose;

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{ title: strings.sendoutsV2.drawers.sendoutDetailsEdit.title }}
      onClose={handleClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <When condition={hasErrorInInitialFetching}>
        <EmptyPlaceholder
          subtitle={strings.formatString(strings.sendoutsV2.form.initialFetchingError, {
            documentType: 'Sendout'
          })}
          height="100%"
        />
      </When>
      <When condition={isFetching}>
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      </When>
      <When condition={isReady}>
        <FormContext {...form}>
          <SendoutDetailsEditForm
            jobOrder={jobOrder}
            candidate={candidate}
            onStatusSelected={handleStatusSelected}
          />
        </FormContext>
      </When>
      <When condition={declinationDrawerState.isOpen}>
        <SendoutDeclinationReasonsDrawer
          initialValues={declinationDrawerState.initialValues}
          onClose={closeDeclinationDrawer}
          onSaved={handleDeclinationDrawerSaved}
          status={declinationDrawerState.pendingStatus}
        />
      </When>
    </FPDrawer>
  );
};
